import FPS from './fps'
import Bag from './bag'
import Minimap from './minimap'
import Chest from './chest'
import Drops from '../drops'
import Terrain from '../terrain'
import Block from '../terrain/mesh/block'
import Control from '../control'
import { GameMode } from '../control'
import { Mode } from '../player'
import { InventorySlot } from '../items'
import Joystick from './joystick'
import { isMobile } from '../utils'
import * as THREE from 'three'
import Mobs from '../mobs'

const UI_TEXT = {
  play: '开始游戏',
  resume: '继续游戏',
  loadGame: '读取存档',
  saveAndExit: '保存并退出',
  renderDistance: '渲染距离',
  fov: '视野范围',
  music: '音乐',
  on: '开',
  off: '关'
}

export default class UI {
  constructor(terrain: Terrain, control: Control) {
    this.controlRef = control
    this.fps = new FPS()
    this.bag = new Bag(open => {
      this.inventoryOpen = open
      if (open && document.pointerLockElement) {
        !isMobile && control.control.unlock()
      }
      if (!open && !document.pointerLockElement) {
        this.onPause()
      }
    })
    this.joystick = new Joystick(control)
    this.minimap = new Minimap(terrain, control.camera)
    this.chest = new Chest(this.bag, open => {
      this.inventoryOpen = open
      if (open && document.pointerLockElement) {
        !isMobile && control.control.unlock()
      }
      if (!open && !document.pointerLockElement) {
        this.onPause()
      }
    })

    this.crossHair.className = 'cross-hair'
    this.crossHair.innerHTML = '+'
    this.crossHair.classList.add('hidden')
    document.body.appendChild(this.crossHair)

    this.status.className = 'status-panel'
    this.status.classList.add('hidden')
    document.body.appendChild(this.status)

    // play
    this.play?.addEventListener('click', () => {
      if (this.play?.innerHTML === UI_TEXT.play) {
        this.onPlay()

        // reset game
        terrain.noise.seed = Math.random()
        terrain.noise.stoneSeed = Math.random()
        terrain.noise.treeSeed = Math.random()
        terrain.noise.coalSeed = Math.random()
        terrain.noise.leafSeed = Math.random()
        terrain.customBlocks = []
        terrain.structuresBuilt.clear()
        this.chest.store.clear()
        this.drops?.clear()
        terrain.initBlocks()
        terrain.generate()
        terrain.camera.position.x = 8
        terrain.camera.position.y = 40
        terrain.camera.position.z = 8
        control.player.setMode(Mode.walking)
      }
      !isMobile && control.control.lock()
    })

    // save load
    this.save?.addEventListener('click', () => {
      if (this.save?.innerHTML === UI_TEXT.saveAndExit) {
        // save game
        window.localStorage.setItem(
          'block',
          JSON.stringify(terrain.customBlocks)
        )
        window.localStorage.setItem('seed', JSON.stringify(terrain.noise.seed))

        window.localStorage.setItem(
          'position',
          JSON.stringify({
            x: terrain.camera.position.x,
            y: terrain.camera.position.y,
            z: terrain.camera.position.z
          })
        )
        window.localStorage.setItem(
          'chest',
          JSON.stringify(Array.from(this.chest.store.entries()))
        )

        // ui update
        this.onExit()
        this.onSave()
      } else {
        // load game
        terrain.noise.seed =
          Number(window.localStorage.getItem('seed')) ?? Math.random()

        const customBlocks =
          (JSON.parse(
            window.localStorage.getItem('block') || 'null'
          ) as Block[]) ?? []

        terrain.customBlocks = customBlocks
        terrain.structuresBuilt.clear()
        this.chest.store = new Map<string, InventorySlot[]>(
          JSON.parse(window.localStorage.getItem('chest') || '[]')
        )
        this.drops?.clear()
        terrain.initBlocks()
        terrain.generate()

        const position =
          (JSON.parse(window.localStorage.getItem('position') || 'null') as {
            x: number
            y: number
            z: number
          }) ?? null

        position && (terrain.camera.position.x = position.x)
        position && (terrain.camera.position.y = position.y)
        position && (terrain.camera.position.z = position.z)

        // ui update
        this.onPlay()
        this.onLoad()
        !isMobile && control.control.lock()
      }
    })

    // guide
    this.feature?.addEventListener('click', () => {
      this.features?.classList.remove('hidden')
    })
    this.back?.addEventListener('click', () => {
      this.features?.classList.add('hidden')
    })

    // setting
    this.setting?.addEventListener('click', () => {
      this.settings?.classList.remove('hidden')
    })
    this.settingBack?.addEventListener('click', () => {
      this.settings?.classList.add('hidden')
    })

    // render distance
    this.distanceInput?.addEventListener('input', (e: Event) => {
      if (this.distance && e.target instanceof HTMLInputElement) {
        this.distance.innerHTML = `${UI_TEXT.renderDistance}：${e.target.value}`
      }
    })

    // fov
    this.fovInput?.addEventListener('input', (e: Event) => {
      if (this.fov && e.target instanceof HTMLInputElement) {
        this.fov.innerHTML = `${UI_TEXT.fov}：${e.target.value}`
        control.camera.fov = parseInt(e.target.value)
        control.camera.updateProjectionMatrix()
      }
    })

    // music
    this.musicInput?.addEventListener('input', (e: Event) => {
      if (this.fov && e.target instanceof HTMLInputElement) {
        const disabled = e.target.value === '0'
        control.audio.disabled = disabled
        this.music!.innerHTML = `${UI_TEXT.music}：${
          disabled ? UI_TEXT.off : UI_TEXT.on
        }`
      }
    })

    // apply settings
    this.settingBack?.addEventListener('click', () => {
      if (this.distanceInput instanceof HTMLInputElement) {
        terrain.distance = parseInt(this.distanceInput.value)
        terrain.maxCount =
          (terrain.distance * terrain.chunkSize * 2 + terrain.chunkSize) ** 2 +
          500

        terrain.initBlocks()
        terrain.generate()
        terrain.scene.fog = new THREE.Fog(
          0x87ceeb,
          1,
          terrain.distance * 24 + 24
        )
      }
    })

    // menu and fullscreen
    document.body.addEventListener('keydown', (e: KeyboardEvent) => {
      // menu
      if (e.key === 'Escape' && document.pointerLockElement) {
        !isMobile && control.control.unlock()
      }

      // fullscreen
      if (e.key === 'f') {
        if (document.fullscreenElement) {
          document.exitFullscreen()
        } else {
          document.body.requestFullscreen()
        }
      }
    })

    // exit
    this.exit?.addEventListener('click', () => {
      this.onExit()
    })

    // play / pause handler
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement) {
        this.onPlay()
      } else if (this.inventoryOpen) {
        this.menu?.classList.add('hidden')
      } else {
        this.onPause()
      }
    })

    // disable context menu
    document.addEventListener('contextmenu', e => {
      e.preventDefault()
    })

    // fallback lock handler
    document.querySelector('canvas')?.addEventListener('click', (e: Event) => {
      e.preventDefault()
      !isMobile && control.control.lock()
    })
  }

  fps: FPS
  bag: Bag
  joystick: Joystick
  minimap: Minimap
  chest: Chest
  drops?: Drops

  menu = document.querySelector('.menu')
  crossHair = document.createElement('div')
  status = document.createElement('div')

  // buttons
  play = document.querySelector('#play')
  control = document.querySelector('#control')
  setting = document.querySelector('#setting')
  feature = document.querySelector('#feature')
  back = document.querySelector('#back')
  exit = document.querySelector('#exit')
  save = document.querySelector('#save')

  // modals
  saveModal = document.querySelector('.save-modal')
  loadModal = document.querySelector('.load-modal')
  settings = document.querySelector('.settings')
  features = document.querySelector('.features')

  // settings
  distance = document.querySelector('#distance')
  distanceInput = document.querySelector('#distance-input')

  fov = document.querySelector('#fov')
  fovInput = document.querySelector('#fov-input')

  music = document.querySelector('#music')
  musicInput = document.querySelector('#music-input')

  settingBack = document.querySelector('#setting-back')
  lastStatus = ''
  inventoryOpen = false

  onPlay = () => {
    isMobile && this.joystick.init()
    this.menu?.classList.add('hidden')
    this.menu?.classList.remove('start')
    this.play && (this.play.innerHTML = UI_TEXT.resume)
    this.crossHair.classList.remove('hidden')
    this.status.classList.remove('hidden')
    this.fps.setActive(true)
    this.bag.setActive(true)
    this.minimap.setActive(true)
    this.mobs?.setActive(true)
    this.feature?.classList.add('hidden')
  }

  onPause = () => {
    this.menu?.classList.remove('hidden')
    this.crossHair.classList.add('hidden')
    this.status.classList.add('hidden')
    this.fps.setActive(false)
    this.bag.setActive(false)
    this.minimap.setActive(false)
    this.mobs?.setActive(false)
    this.save && (this.save.innerHTML = UI_TEXT.saveAndExit)
  }

  onExit = () => {
    this.menu?.classList.add('start')
    this.status.classList.add('hidden')
    this.fps.setActive(false)
    this.bag.setActive(false)
    this.minimap.setActive(false)
    this.mobs?.setActive(false)
    this.play && (this.play.innerHTML = UI_TEXT.play)
    this.save && (this.save.innerHTML = UI_TEXT.loadGame)
    this.feature?.classList.remove('hidden')
  }

  onSave = () => {
    this.saveModal?.classList.remove('hidden')
    setTimeout(() => {
      this.saveModal?.classList.add('show')
    })
    setTimeout(() => {
      this.saveModal?.classList.remove('show')
    }, 1000)

    setTimeout(() => {
      this.saveModal?.classList.add('hidden')
    }, 1350)
  }

  onLoad = () => {
    this.loadModal?.classList.remove('hidden')
    setTimeout(() => {
      this.loadModal?.classList.add('show')
    })
    setTimeout(() => {
      this.loadModal?.classList.remove('show')
    }, 1000)

    setTimeout(() => {
      this.loadModal?.classList.add('hidden')
    }, 1350)
  }

  update = () => {
    this.fps.update()
    this.minimap.update()
    const health = this.controlRef?.health ?? 20
    const mode = this.controlRef?.gameMode ?? GameMode.creative
    const status = `
      <div>模式：${mode === GameMode.survival ? '生存' : '创造'}（C 切换）</div>
      <div class="player-health ${mode === GameMode.survival ? '' : 'hidden'}">
        ${new Array(10)
          .fill(null)
          .map((_, index) => (index * 2 < health ? '♥' : '♡'))
          .join('')}
      </div>
    `
    if (status !== this.lastStatus) {
      this.status.innerHTML = status
      this.lastStatus = status
    }
  }

  controlRef?: Control
  mobs?: Mobs

  setMobs = (mobs: Mobs) => {
    this.mobs = mobs
  }

  setDrops = (drops: Drops) => {
    this.drops = drops
  }
}
